import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dequal } from 'dequal';
import { tinctApi, useGetPageChatMessages } from '@tinct/core-api';
import { createAiChatEventBus } from '@tinct/web-ai';
import type { ChatMessage } from '@tinct/core-api-models';
import type { AiChatEventBus } from '@tinct/web-ai';
import { createChatStreamHandlers } from '../../../utils/chat-stream-handlers';

const PAGE_SIZE = 20;

export interface UsePageChatOptions {
  pageId: string | undefined;
  /**
   * Called when a turn was sent with a block context but the stream echoed no
   * `context` block back — the selection didn't resolve server-side (stale
   * id) and the turn ran globally, so the host should clear its selection UI.
   */
  onContextIgnored?: () => void;
}

export interface UsePageChatReturn {
  messages: ChatMessage[] | undefined;
  eventBus: AiChatEventBus;
  isLoadingMore: boolean;
  hasMore: boolean;
  /**
   * `blockId` is the selected core block's id, sent as structured context
   * (`{ block_id }`) — never composed into the message text. The backend
   * resolves it to the block's variant on this page. Null/undefined =
   * global ask.
   */
  handleSendMessage: (message: string, blockId?: string | null) => void;
  handleLoadMore: () => void;
}

/**
 * One conversation per page variant — the agent can edit any block of the
 * page, so the chat is keyed by page alone.
 */
export function usePageChat({ pageId, onContextIgnored }: UsePageChatOptions): UsePageChatReturn {
  const eventBus = useMemo(() => createAiChatEventBus(), []);

  // Ref so `handleSendMessage` keeps a stable identity across renders.
  const onContextIgnoredRef = useRef(onContextIgnored);
  useEffect(() => {
    onContextIgnoredRef.current = onContextIgnored;
  }, [onContextIgnored]);

  // ==========================================================================
  // Message state
  // ==========================================================================

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [nextOffset, setNextOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // ==========================================================================
  // Fetch initial messages (newest first — backend returns created DESC)
  // ==========================================================================

  const { data: initialData, isFetched } = useGetPageChatMessages(
    {
      staticParams: { pageId: pageId ?? '' },
      offset: 0,
      limit: PAGE_SIZE,
    },
    {
      refetchOnMount: 'always',
      refetchOnReconnect: true,
    }
  );

  const [prevData, setPrevData] = useState<ChatMessage[]>();
  if (initialData && !dequal(initialData.data, prevData)) {
    setPrevData(initialData.data);

    const total = initialData.pagination.count;
    if (total > 0) {
      setMessages([...initialData.data].reverse());
      setTotalCount(total);
      setNextOffset(PAGE_SIZE);
      setHasMore(total > PAGE_SIZE);
    }
  }

  // ==========================================================================
  // Load more (older messages — next page in DESC order)
  // ==========================================================================

  const handleLoadMore = useCallback(() => {
    if (!pageId || isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);

    tinctApi.pages.getPageChatAgentMessages({
      staticParams: { pageId },
      offset: nextOffset,
      limit: PAGE_SIZE,
    }).then((response) => {
      setMessages((prev) => [...[...response.data].reverse(), ...prev]);
      const newOffset = nextOffset + response.data.length;
      setNextOffset(newOffset);
      setHasMore(newOffset < totalCount);
      setIsLoadingMore(false);
    }).catch(() => {
      setIsLoadingMore(false);
    });
  }, [pageId, isLoadingMore, hasMore, nextOffset, totalCount]);

  // ==========================================================================
  // Send message
  // ==========================================================================

  const handleSendMessage = useCallback((message: string, blockId?: string | null) => {
    if (!pageId) return;
    const { onEvent, onError, onComplete } = createChatStreamHandlers(eventBus, {
      // Only meaningful on a scoped turn: no context echo back means the
      // selection was ignored server-side.
      onContextOutcome: blockId
        ? (echoed) => {
            if (!echoed) onContextIgnoredRef.current?.();
          }
        : undefined,
    });
    tinctApi.pages.postPageMessageChat(
      pageId,
      message,
      blockId ? { blockId } : null,
      onEvent,
      onError,
      onComplete
    );
  }, [pageId, eventBus]);

  return {
    messages: isFetched ? messages : undefined,
    eventBus,
    isLoadingMore,
    hasMore,
    handleSendMessage,
    handleLoadMore,
  };
}
